/**
 * Centralized API endpoint paths.
 *
 * Conventions:
 *   - Group by domain (AUTH, PROPERTIES, ...). One group per module.
 *   - Static paths are plain string constants.
 *   - Parameterized paths are arrow functions so the call site reads as a
 *     normal call: `PROPERTY_API_URLS.detail(id)`. Avoids template-string
 *     drift across the codebase.
 *   - Always prefixed with /api/v1 — the API mounts everything there.
 *
 * Adding a new endpoint:
 *   1. Add it to (or create) the matching *_API_URLS group below.
 *   2. Reference it from `<module>/api/<module>.service.ts` — never inline
 *      the path string in a service or hook.
 */

const V1 = '/api/v1';

export const AUTH_API_URLS = {
    devLogin:        `${V1}/auth/login`,
    verifyTwoFactor: `${V1}/auth/login/verify-2fa`,
    resendTwoFactor: `${V1}/auth/login/resend-2fa`,
    signOut:         `${V1}/auth/sign-out`,
    forgotPassword:  `${V1}/auth/forgot-password`,
    resetPassword:   `${V1}/auth/reset-password`,
} as const;

export const TENANT_API_URLS = {
    me:          `${V1}/tenants/me`,
    plan:        `${V1}/tenants/me/plan`,
    permissions: `${V1}/tenants/me/permissions`,
} as const;

export const WORKSPACE_API_URLS = {
    list:   `${V1}/workspaces`,
    switch: `${V1}/workspaces/switch`,
} as const;

/** Inventory → Projects (a workspace's own grouping of its properties). */
export const PROJECT_API_URLS = {
    list:       `${V1}/projects`,
    create:     `${V1}/projects`,
    detail:     (id: string) => `${V1}/projects/${encodeURIComponent(id)}`,
    properties: (id: string) => `${V1}/projects/${encodeURIComponent(id)}/properties`,
    detach:     (id: string) => `${V1}/projects/${encodeURIComponent(id)}/properties/detach`,
} as const;

export const PROPERTY_API_URLS = {
    list:   `${V1}/properties`,
    /** Location fields of the newest-created property, to seed the create form. */
    latestDefaults: `${V1}/properties/latest-defaults`,
    detail: (id: string) => `${V1}/properties/${encodeURIComponent(id)}`,
    create: `${V1}/properties`,
    bulkImport: `${V1}/properties/bulk-import`,
    update: (id: string) => `${V1}/properties/${encodeURIComponent(id)}`,
    remove: (id: string) => `${V1}/properties/${encodeURIComponent(id)}`,
    externalRefAvailability: `${V1}/properties/external-ref/availability`,
} as const;

export const MEDIA_API_URLS = {
    upload: `${V1}/media`,
    remove: (id: string) => `${V1}/media/${encodeURIComponent(id)}`,
} as const;

export const DOCUMENT_API_URLS = {
    upload: `${V1}/documents`,
    remove: (id: string) => `${V1}/documents/${encodeURIComponent(id)}`,
} as const;

export const BUILDER_API_URLS = {
    list:   `${V1}/builders`,
    detail: (id: string) => `${V1}/builders/${encodeURIComponent(id)}`,
    create: `${V1}/builders`,
    update: (id: string) => `${V1}/builders/${encodeURIComponent(id)}`,
    remove: (id: string) => `${V1}/builders/${encodeURIComponent(id)}`,
    /** Owner-set password for a builder login. */
    password: (id: string) => `${V1}/builders/${encodeURIComponent(id)}/password`,
} as const;

/**
 * Location Segment catalogue — State → Territory → City → Community.
 * States are global; the rest are scoped to the current workspace. The three
 * create endpoints are find-or-create (they reuse an existing global record
 * rather than duplicating it).
 */
export const LOCATION_API_URLS = {
    states:      `${V1}/locations/states`,
    territories: `${V1}/locations/territories`,
    cities:      `${V1}/locations/cities`,
    communities: `${V1}/locations/communities`,
} as const;

/** Settings → Lead Routing. */
export const LEAD_ROUTING_API_URLS = {
    config:      `${V1}/lead-routing`,
    assignments: `${V1}/lead-routing/assignments`,
    settings:    `${V1}/lead-routing/settings`,
    roundRobin:  `${V1}/lead-routing/round-robin`,
} as const;

export const SALES_TEAM_API_URLS = {
    list:   `${V1}/sales-team`,
    detail: (id: string) => `${V1}/sales-team/${encodeURIComponent(id)}`,
    create: `${V1}/sales-team`,
    update: (id: string) => `${V1}/sales-team/${encodeURIComponent(id)}`,
    remove: (id: string) => `${V1}/sales-team/${encodeURIComponent(id)}`,
    /** Manager-set password for a member's dashboard login. */
    password: (id: string) => `${V1}/sales-team/${encodeURIComponent(id)}/password`,
} as const;

export const LEAD_API_URLS = {
    list:   `${V1}/leads`,
    detail: (id: string) => `${V1}/leads/${encodeURIComponent(id)}`,
    create: `${V1}/leads`,
    bulkImport: `${V1}/leads/bulk-import`,
    update: (id: string) => `${V1}/leads/${encodeURIComponent(id)}`,
    remove: (id: string) => `${V1}/leads/${encodeURIComponent(id)}`,
    status: (id: string) => `${V1}/leads/${encodeURIComponent(id)}/status`,
    assign: (id: string) => `${V1}/leads/${encodeURIComponent(id)}/assign`,
    notes:  (id: string) => `${V1}/leads/${encodeURIComponent(id)}/notes`,
    message:(id: string) => `${V1}/leads/${encodeURIComponent(id)}/message`,
    bulkMessage: `${V1}/leads/bulk-message`,
    // Lead preview → Attachments tab
    attachments:    (id: string) => `${V1}/leads/${encodeURIComponent(id)}/attachments`,
    attachment:     (id: string, attachmentId: string) =>
        `${V1}/leads/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`,
    attachmentFile: (id: string, attachmentId: string) =>
        `${V1}/leads/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}/file`,
    // Lead preview → Tasks tab
    tasks:          (id: string) => `${V1}/leads/${encodeURIComponent(id)}/tasks`,
    task:           (id: string, taskId: string) =>
        `${V1}/leads/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}`,
    taskFollowUps:  (id: string, taskId: string) =>
        `${V1}/leads/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}/follow-ups`,
    taskTimeLogs:   (id: string, taskId: string) =>
        `${V1}/leads/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}/time-logs`,
    taskTimer:      (id: string, taskId: string) =>
        `${V1}/leads/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}/timer`,
} as const;

export const LEAD_STATUS_API_URLS = {
    list:    `${V1}/lead-statuses`,
    create:  `${V1}/lead-statuses`,
    update:  (id: string) => `${V1}/lead-statuses/${encodeURIComponent(id)}`,
    remove:  (id: string) => `${V1}/lead-statuses/${encodeURIComponent(id)}`,
    reorder: `${V1}/lead-statuses/reorder`,
} as const;

export const USER_API_URLS = {
    list: `${V1}/users`,
} as const;

/** "Request access" from the no-access dialog — notifies workspace admins. */
export const ACCESS_REQUEST_API_URLS = {
    create: `${V1}/access-requests`,
} as const;

/** Role Matrix — tenant-scoped roles + their module × action grants. */
export const ROLE_API_URLS = {
    list:   `${V1}/roles`,
    detail: (id: string) => `${V1}/roles/${encodeURIComponent(id)}`,
    create: `${V1}/roles`,
    update: (id: string) => `${V1}/roles/${encodeURIComponent(id)}`,
    remove: (id: string) => `${V1}/roles/${encodeURIComponent(id)}`,
} as const;

export const NOTIFICATION_API_URLS = {
    list:        `${V1}/notifications`,
    markRead:    (id: string) => `${V1}/notifications/${encodeURIComponent(id)}/read`,
    markAllRead: `${V1}/notifications/read-all`,
} as const;

export const TOUR_API_URLS = {
    list: `${V1}/tours`,
} as const;

export const QR_API_URLS = {
    list:   `${V1}/qr-codes`,
    detail: (id: string) => `${V1}/qr-codes/${encodeURIComponent(id)}`,
    create: `${V1}/qr-codes`,
    update: (id: string) => `${V1}/qr-codes/${encodeURIComponent(id)}`,
    remove: (id: string) => `${V1}/qr-codes/${encodeURIComponent(id)}`,
    analytics: (id: string) => `${V1}/qr-codes/${encodeURIComponent(id)}/analytics`,
    scansCsv:  (id: string) => `${V1}/qr-codes/${encodeURIComponent(id)}/scans.csv`,
} as const;

export const CUSTOM_COLUMN_API_URLS = {
    list:   `${V1}/custom-columns`,
    values: `${V1}/custom-columns/values`,
    create: `${V1}/custom-columns`,
    update: (id: string) => `${V1}/custom-columns/${encodeURIComponent(id)}`,
    remove: (id: string) => `${V1}/custom-columns/${encodeURIComponent(id)}`,
    setValue: `${V1}/custom-columns/values`,
} as const;

export const SETTINGS_API_URLS = {
    notifications:   `${V1}/settings/notifications`,
    // Settings → Notifications: the signed-in member's OWN per-event email
    // preferences. `notifications` above is the tenant-wide lead fan-out config.
    notificationPreferences: `${V1}/settings/notification-preferences`,
    profile:         `${V1}/settings/profile`,
    profilePassword: `${V1}/settings/profile/password`,

    // Two-factor authentication (TOTP) — account protection for the signed-in user.
    twoFactor:            `${V1}/settings/2fa`,
    twoFactorSetup:       `${V1}/settings/2fa/setup`,
    twoFactorEnable:      `${V1}/settings/2fa/enable`,
    twoFactorDisable:     `${V1}/settings/2fa/disable`,
    twoFactorBackupCodes: `${V1}/settings/2fa/backup-codes/regenerate`,
    branding:           `${V1}/settings/branding`,
    // Per-user, per-workspace table layout (column order + hidden columns).
    tablePrefs:      (table: string) => `${V1}/settings/table-prefs/${encodeURIComponent(table)}`,
    tablePrefsSave:  `${V1}/settings/table-prefs`,
    integrations:       `${V1}/settings/integrations`,
    integrationsHealth: `${V1}/settings/integrations/health`,

    // Integrations → Lead Sync: push captured leads into the tenant's own
    // email-marketing platform (SendGrid Marketing Campaigns).
    emailMarketing:         `${V1}/settings/integrations/email-marketing`,
    emailMarketingProviders: `${V1}/settings/integrations/email-marketing/providers`,
    emailMarketingLists:    `${V1}/settings/integrations/email-marketing/lists`,
    emailMarketingConnect:  `${V1}/settings/integrations/email-marketing/connect`,
    emailMarketingBackfill: `${V1}/settings/integrations/email-marketing/backfill`,
    welcomeSequence:      `${V1}/welcome-sequence`,
    welcomeSequenceStats: `${V1}/welcome-sequence/stats`,
    accountDeletion:    `${V1}/settings/account/deletion`,

    // Sending Identity — Email (SendGrid)
    emailIdentities:       `${V1}/settings/email-identities`,
    emailIdentity:        (id: string) => `${V1}/settings/email-identities/${encodeURIComponent(id)}`,
    emailIdentityVerify:  (id: string) => `${V1}/settings/email-identities/${encodeURIComponent(id)}/verify`,
    emailIdentityDefault: (id: string) => `${V1}/settings/email-identities/${encodeURIComponent(id)}/default`,

    // Sending Identity — SMS (Twilio numbers)
    sendingIdentities:       `${V1}/settings/sending-identities`,
    sendingIdentity:        (id: string) => `${V1}/settings/sending-identities/${encodeURIComponent(id)}`,
    sendingIdentityVerify:  (id: string) => `${V1}/settings/sending-identities/${encodeURIComponent(id)}/verify-ownership`,
    sendingIdentityDefault: (id: string) => `${V1}/settings/sending-identities/${encodeURIComponent(id)}/default`,
} as const;

// Email Sending Identity (my SendGrid domain-auth model). SMS lives under
// SETTINGS_API_URLS.sendingIdentities (re-architect's Twilio module).
export const EMAIL_IDENTITY_API_URLS = {
    list:    `${V1}/sender-identities`,
    create:  `${V1}/sender-identities`,
    verify:  (id: string) => `${V1}/sender-identities/${encodeURIComponent(id)}/verify`,
    /** Re-issue the CNAMEs under a different DKIM selector (conflict recovery). */
    dkimSelector: (id: string) => `${V1}/sender-identities/${encodeURIComponent(id)}/dkim-selector`,
    default: (id: string) => `${V1}/sender-identities/${encodeURIComponent(id)}/default`,
    remove:  (id: string) => `${V1}/sender-identities/${encodeURIComponent(id)}`,
} as const;

export const WEB_ANALYTICS_API_URLS = {
    overview: `${V1}/web-analytics/overview`,
} as const;

export const SUPPORT_API_URLS = {
    list:   `${V1}/support`,
    create: `${V1}/support`,
    reply:  (id: string) => `${V1}/support/${encodeURIComponent(id)}/messages`,
} as const;

export const ANALYTICS_API_URLS = {
    overview:       `${V1}/analytics/overview`,
    developerDashboard: `${V1}/analytics/developer-dashboard`,
    builderDashboard: `${V1}/analytics/builder-dashboard`,
    agentDashboard: `${V1}/analytics/agent-dashboard`,
    summary:        `${V1}/analytics/summary`,
    funnel:         `${V1}/analytics/funnel`,
    scansTimeseries: `${V1}/analytics/scans-timeseries`,
    builderHealth:  `${V1}/analytics/builder-health`,
    propertyPerf:   `${V1}/analytics/property-performance`,
    qrPerf:         `${V1}/analytics/qr-performance`,
    // Mounted ahead of the dashboard router with its own 25mb JSON parser —
    // the chart PNGs travel base64-encoded (see routes/index.ts).
    emailReport:    `${V1}/analytics/report/email`,
} as const;

export const SIGNUP_API_URLS = {
    tenant:      `${V1}/signup/tenant`,
    verifySend:  `${V1}/signup/verify/send`,
    verifyCheck: `${V1}/signup/verify/check`,
} as const;

export const PLANS_API_URLS = {
    list: `${V1}/plans`,
} as const;

export const BILLING_API_URLS = {
    subscription: `${V1}/billing/subscription`,
    usage:        `${V1}/billing/usage`,
    summary:      `${V1}/billing/summary`,
    invoices:     `${V1}/billing/invoices`,
    invoicesExport: `${V1}/billing/invoices/export`,
    paymentReceipt: (paymentId: string) => `${V1}/billing/payments/${encodeURIComponent(paymentId)}/receipt.pdf`,
    promo:        `${V1}/billing/promo`,
    checkout:     `${V1}/billing/checkout`,
    changePlan:   `${V1}/billing/change-plan`,
    sync:         `${V1}/billing/sync`,
    portal:       `${V1}/billing/portal`,
    cancel:       `${V1}/billing/cancel`,
    reactivate:   `${V1}/billing/reactivate`,
    addOns:       `${V1}/billing/add-ons`,
    paymentMethods: `${V1}/billing/payment-methods`,
    paymentMethodsDefault: `${V1}/billing/payment-methods/default`,
} as const;

export const ONBOARDING_API_URLS = {
    progress: `${V1}/onboarding/progress`,
    update:   `${V1}/onboarding/progress`,
} as const;

/**
 * Help-centre content. Authored through the admin routes, read by every tenant
 * through the dashboard route — same rows, filtered to published.
 */
export const HELP_API_URLS = {
    published: `${V1}/help`,
    admin:     `${V1}/admin/help`,
    create:    (kind: string) => `${V1}/admin/help/${kind}`,
    entry:     (kind: string, id: string) => `${V1}/admin/help/${kind}/${encodeURIComponent(id)}`,
} as const;

export const ADMIN_API_URLS = {
    profile: `${V1}/admin/profile`,
    profilePassword: `${V1}/admin/profile/password`,
    stats: `${V1}/admin/stats`,
    revenueTrend: `${V1}/admin/revenue-trend`,
    plans: `${V1}/admin/plans`,
    tenants: `${V1}/admin/tenants`,
    tenantsTrash: `${V1}/admin/tenants/trash`,
    tickets: `${V1}/admin/tickets`,
    createTicket: `${V1}/admin/tickets`,
    ticketReply: (id: string) => `${V1}/admin/tickets/${encodeURIComponent(id)}/messages`,
    ticketStatus: (id: string) => `${V1}/admin/tickets/${encodeURIComponent(id)}/status`,
    updateStatus: (id: string) => `${V1}/admin/tenants/${encodeURIComponent(id)}/status`,
    updatePlan: (id: string) => `${V1}/admin/tenants/${encodeURIComponent(id)}/plan`,
    updateQrLimit: (id: string) => `${V1}/admin/tenants/${encodeURIComponent(id)}/qr-limit`,
    grantQr: (id: string) => `${V1}/admin/tenants/${encodeURIComponent(id)}/grant-qr`,
    refund: (id: string) => `${V1}/admin/tenants/${encodeURIComponent(id)}/refund`,
    restoreTenant: (id: string) => `${V1}/admin/tenants/${encodeURIComponent(id)}/restore`,
    deleteTenant: (id: string) => `${V1}/admin/tenants/${encodeURIComponent(id)}`,
    // Plans & Pricing management (plan CRUD; add-ons are edit-only), incl. inactive rows.
    planCatalog: `${V1}/admin/plans/catalog`,
    createPlan: `${V1}/admin/plans`,
    updateCatalogPlan: (id: string) => `${V1}/admin/plans/${encodeURIComponent(id)}`,
    addOnCatalog: `${V1}/admin/add-ons`,
    updateCatalogAddOn: (id: string) => `${V1}/admin/add-ons/${encodeURIComponent(id)}`,
    // Client account billing — subscription snapshot, ledger, manual actions.
    tenantBilling: (id: string) => `${V1}/admin/tenants/${encodeURIComponent(id)}/billing`,
    tenantPayments: (id: string) => `${V1}/admin/tenants/${encodeURIComponent(id)}/payments`,
    activatePlan: (id: string) => `${V1}/admin/tenants/${encodeURIComponent(id)}/activate-plan`,
    tenantAddOns: (id: string) => `${V1}/admin/tenants/${encodeURIComponent(id)}/add-ons`,
    tenantAddOn: (id: string, addOnId: string) =>
        `${V1}/admin/tenants/${encodeURIComponent(id)}/add-ons/${encodeURIComponent(addOnId)}`,
    editPayment: (paymentId: string) => `${V1}/admin/payments/${encodeURIComponent(paymentId)}`,
    voidPayment: (paymentId: string) => `${V1}/admin/payments/${encodeURIComponent(paymentId)}/void`,
    paymentReceipt: (paymentId: string) => `${V1}/admin/payments/${encodeURIComponent(paymentId)}/receipt.pdf`,
    auditLogs: `${V1}/admin/audit-logs`,
    // Security — 2FA for the admin's own login.
    twoFactor:            `${V1}/admin/security/2fa`,
    twoFactorSetup:       `${V1}/admin/security/2fa/setup`,
    twoFactorEnable:      `${V1}/admin/security/2fa/enable`,
    twoFactorDisable:     `${V1}/admin/security/2fa/disable`,
    twoFactorBackupCodes: `${V1}/admin/security/2fa/backup-codes/regenerate`,
    // Lead database — per-client counts, one client's leads, code-verified export.
    leadSummary:      `${V1}/admin/leads/summary`,
    leadExportCode:   `${V1}/admin/leads/export-code`,
    tenantLeads:      (id: string) => `${V1}/admin/tenants/${encodeURIComponent(id)}/leads`,
    tenantLeadExport: (id: string) => `${V1}/admin/tenants/${encodeURIComponent(id)}/leads/export`,
} as const;
